import { useState, useEffect, useRef, useCallback } from "react";
import { Upload, Loader2, ImageOff, X, Zap, Undo2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "../../components/common/ui";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import api, { formatApiError } from "../../lib/api";

export default function AdminUnmatchedMedia() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [useFirstAsPrimary, setUseFirstAsPrimary] = useState(true);
  const [bulkActions, setBulkActions] = useState([]);
  const [rollingBack, setRollingBack] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/unmatched-media").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/media-import/bulk-actions").then(({ data }) => setBulkActions(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPreview = async () => {
    setLoadingPreview(true); setBulkResult(null);
    try {
      const { data } = await api.get("/admin/media-import/confident-preview");
      setPreview(data); setPreviewOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not build preview");
    } finally { setLoadingPreview(false); }
  };

  const runAttach = async () => {
    setExecuting(true);
    try {
      const { data } = await api.post("/admin/media-import/attach-confident", { use_first_as_primary: useFirstAsPrimary });
      setBulkResult(data); setPreview(null);
      toast.success(`Attached ${data.attached} images to ${data.products_affected} products`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Attach failed");
    } finally { setExecuting(false); }
  };

  const rollbackAction = async (a) => {
    if (!window.confirm("Undo this bulk attachment? Attached images will be detached and returned to the unmatched queue.")) return;
    setRollingBack(a.action_id);
    try {
      const { data } = await api.post(`/admin/media-import/bulk-actions/${a.action_id}/rollback`);
      toast.success(`Rolled back — ${data.removed} images detached`); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rollback failed");
    } finally { setRollingBack(null); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const { data } = await api.post("/admin/media-import/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setSummary(data.summary);
      toast.success(`Matched ${data.summary.matched}, unmatched ${data.summary.unmatched}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const assign = async (um, productId) => {
    try {
      await api.post(`/admin/unmatched-media/${um.unmatched_id}/assign`, { product_id: productId });
      toast.success("Image assigned"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const ignore = async (um) => {
    try { await api.post(`/admin/unmatched-media/${um.unmatched_id}/ignore`); load(); }
    catch (err) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-unmatched-media">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl text-white">Product Media Import</h1>
          <p className="mt-2 text-muted-foreground">Upload images or a ZIP. Filenames are matched to products by item number/SKU. Ambiguous matches (duplicate item numbers) require manual assignment below.</p>
        </div>
        <input ref={fileRef} type="file" accept="image/*,.zip" multiple hidden onChange={onUpload} data-testid="media-import-file" />
        <div className="flex gap-2">
          <Button data-testid="attach-confident-btn" disabled={loadingPreview} onClick={openPreview} variant="outline" className="border-gold/40 text-gold hover:bg-gold/10 font-semibold gap-2">
            {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Attach All Confident Matches
          </Button>
          <Button data-testid="media-import-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="cf-gold-btn font-semibold gap-2">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Images / ZIP
          </Button>
        </div>
      </div>

      {bulkResult && (
        <GlassCard className="p-5" hover={false} data-testid="bulk-result">
          <div className="flex items-center gap-2 mb-3"><CheckCircle2 className="h-5 w-5 text-emerald-400" /><h3 className="font-playfair text-lg text-white">Bulk attach complete</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-sm">
            <div><p className="text-emerald-400 font-playfair text-xl">{bulkResult.attached}</p><p className="text-xs text-muted-foreground">Attached</p></div>
            <div><p className="text-gold font-playfair text-xl">{bulkResult.primary_created}</p><p className="text-xs text-muted-foreground">Primary created</p></div>
            <div><p className="text-sky-400 font-playfair text-xl">{bulkResult.gallery_added}</p><p className="text-xs text-muted-foreground">Gallery added</p></div>
            <div><p className="text-white/60 font-playfair text-xl">{bulkResult.duplicate_skipped}</p><p className="text-xs text-muted-foreground">Duplicates</p></div>
            <div><p className="text-amber font-playfair text-xl">{bulkResult.ambiguous}</p><p className="text-xs text-muted-foreground">Ambiguous</p></div>
            <div><p className="text-white/50 font-playfair text-xl">{bulkResult.unmatched}</p><p className="text-xs text-muted-foreground">Unmatched</p></div>
            <div><p className="text-firered font-playfair text-xl">{bulkResult.failed}</p><p className="text-xs text-muted-foreground">Failed</p></div>
          </div>
        </GlassCard>
      )}

      {summary && (
        <GlassCard className="p-4 flex flex-wrap gap-6" hover={false} data-testid="media-import-summary">
          <span className="text-emerald-400 text-sm">Matched: {summary.matched || 0}</span>
          <span className="text-amber text-sm">Unmatched: {summary.unmatched || 0}</span>
          <span className="text-firered text-sm">Invalid: {summary.invalid || 0}</span>
          <span className="text-white/50 text-sm">Skipped: {summary.skipped || 0}</span>
        </GlassCard>
      )}

      <h2 className="font-playfair text-lg text-white flex items-center gap-2"><ImageOff className="h-4 w-4 text-gold" /> Unmatched Media</h2>
      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…</div>
      ) : items.length === 0 ? (
        <GlassCard className="p-10 text-center text-muted-foreground" hover={false}>No unmatched media. All uploaded images were matched to products.</GlassCard>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((um) => (
            <GlassCard key={um.unmatched_id} className="p-4" hover={false} data-testid={`unmatched-${um.unmatched_id}`}>
              <div className="flex gap-3">
                <img src={um.thumb_url || um.url} alt="" className="h-20 w-20 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm truncate">{um.original_filename}</p>
                  <p className="text-xs text-white/40">key: {um.candidate_key}</p>
                  <p className="text-xs text-amber mt-1">{um.reason}</p>
                </div>
                <button onClick={() => ignore(um)} title="Ignore" data-testid={`ignore-${um.unmatched_id}`} className="text-white/40 hover:text-firered h-6 w-6 grid place-items-center"><X className="h-4 w-4" /></button>
              </div>
              {um.candidates?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <p className="text-xs text-white/50">Candidate products:</p>
                  {um.candidates.map((c) => (
                    <button key={c.product_id} onClick={() => assign(um, c.product_id)} data-testid={`assign-${um.unmatched_id}-${c.product_id}`}
                      className="w-full text-left text-xs bg-white/5 hover:bg-gold/15 border border-white/10 hover:border-gold/40 rounded-md px-2.5 py-1.5 text-white/80 transition-colors">
                      {c.name} <span className="text-white/40">· {c.source_sheet} · {c.sku}</span>
                    </button>
                  ))}
                </div>
              )}
            </GlassCard>
          ))}
        </div>
      )}

      {bulkActions.length > 0 && (
        <div>
          <h2 className="font-playfair text-lg text-white mb-3">Recent bulk attachments</h2>
          <GlassCard className="p-0 overflow-hidden" hover={false}>
            {bulkActions.map((a) => (
              <div key={a.action_id} className="flex items-center gap-4 px-4 py-3 border-b border-white/10 last:border-0" data-testid={`bulk-action-${a.action_id}`}>
                <div className="flex-1 text-sm">
                  <span className="text-white">{a.report?.attached ?? 0} images → {a.report?.products_affected ?? 0} products</span>
                  <span className="text-white/40 text-xs ml-2">{new Date(a.created_at).toLocaleString("en-GB")}</span>
                </div>
                {a.status === "rolled_back" ? (
                  <span className="text-xs text-white/40">Rolled back</span>
                ) : (
                  <button onClick={() => rollbackAction(a)} disabled={rollingBack === a.action_id} data-testid={`bulk-rollback-${a.action_id}`} className="inline-flex items-center gap-1 text-xs text-amber hover:text-gold">
                    {rollingBack === a.action_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />} Undo
                  </button>
                )}
              </div>
            ))}
          </GlassCard>
        </div>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="cf-glass border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="confident-preview-dialog">
          <DialogHeader><DialogTitle className="font-playfair text-xl flex items-center gap-2"><Zap className="h-5 w-5 text-gold" /> Attach Confident Matches — Preview</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[["Files in queue", preview.total_uploaded_in_queue, "text-white"],
                  ["Confident matches", preview.confident_matches, "text-emerald-400"],
                  ["Ambiguous", preview.ambiguous_matches, "text-amber"],
                  ["Unmatched", preview.unmatched_files, "text-white/50"],
                  ["Duplicates (skip)", preview.duplicate_skipped, "text-white/60"],
                  ["Products (1 image)", preview.products_receiving_one, "text-sky-400"],
                  ["Products (gallery)", preview.products_receiving_multiple, "text-sky-400"],
                  ["No primary yet", preview.products_without_primary, "text-gold"]].map(([l, v, t]) => (
                  <div key={l} className="rounded-lg border border-white/10 bg-black/20 p-3"><p className={`font-playfair text-2xl ${t}`}>{v}</p><p className="text-xs text-muted-foreground mt-0.5">{l}</p></div>
                ))}
              </div>

              {preview.confident_matches === 0 ? (
                <p className="text-sm text-amber" data-testid="preview-none">No confident single-match files to attach right now. Ambiguous/unmatched files remain in the queue for manual review.</p>
              ) : (
                <>
                  <div className="max-h-52 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
                    {preview.products.map((p) => (
                      <div key={p.product_id} className="px-3 py-2 text-sm flex items-center gap-2" data-testid={`preview-product-${p.product_id}`}>
                        <span className="text-white flex-1 truncate">{p.product_name}</span>
                        <span className="text-white/50 text-xs">{p.image_count} img{p.image_count > 1 ? "s" : ""}</span>
                        {!p.has_existing_primary && <span className="text-[10px] text-gold border border-gold/40 rounded-full px-1.5">no primary</span>}
                      </div>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-white/80">
                    <Switch data-testid="use-primary-switch" checked={useFirstAsPrimary} onCheckedChange={setUseFirstAsPrimary} />
                    Use first matched image as primary only where no primary image exists
                  </label>
                  <p className="text-xs text-white/40">Existing primary images are never replaced. Files matched only by name, ambiguous or duplicate files are excluded.</p>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)} className="text-white/70">Cancel</Button>
            {preview?.confident_matches > 0 && (
              <Button data-testid="confirm-attach-btn" onClick={runAttach} disabled={executing} className="cf-gold-btn font-semibold gap-2">
                {executing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Attach {preview.confident_matches} images to {preview.products_receiving_one + preview.products_receiving_multiple} products
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
